/**
 * FloatingActionMenu.tsx
 * Menu flottant (Floating Action Pill) qui apparaît sous la sélection
 */
import React, { useCallback } from 'react';
import { useAppStore } from '../../../services/StateService';
import { logger } from '../../../utils/ProfessionalLogger';
import { useAppCoordinatorOptional } from '../../../hooks/useAppCoordinator';
import { FileUploadService } from '../../../services/FileUploadService';
import type { ShapeData } from '../../../types/ShapeTypes';
import {
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignStartVertical as AlignTop,
  AlignCenterVertical as AlignVerticalCenter,
  AlignEndVertical as AlignBottom,
  Eye,
} from 'lucide-react';

interface FloatingActionMenuProps {
  selectionBounds: { x: number; y: number; width: number; height: number } | null;
  selectionIds: string[];
  zoomLevel: number;
  canvasOffset: { x: number; y: number };
  stagePosition: { x: number; y: number };
}

/**
 * Composant FloatingActionMenu
 */
export function FloatingActionMenu({
  selectionBounds,
  selectionIds,
  zoomLevel,
  canvasOffset,
  stagePosition,
}: FloatingActionMenuProps): React.ReactElement {
  const _appCoordinator = useAppCoordinatorOptional();
  const shapes = useAppStore(state => state.shapes);
  const updateShape = useAppStore(state => state.updateShape);
  const updateShapes = useAppStore(state => state.updateShapes);
  const duplicateShapes = useAppStore(state => state.duplicateShapes);
  const deleteShapes = useAppStore(state => state.deleteShapes);
  const beginGesture = useAppStore(state => state.beginGesture);
  const endGesture = useAppStore(state => state.endGesture);
  const canvasWidth = useAppStore(state => state.canvas.templateWidth);
  const canvasHeight = useAppStore(state => state.canvas.templateHeight);
  // PR-12: Actions d'alignement et de distribution
  const alignSelection = useAppStore(state => state.alignSelection);
  const distributeSelection = useAppStore(state => state.distributeSelection);
  // PR-13: Actions de rotation et de scale
  const rotateSelection = useAppStore(state => state.rotateSelection);
  const scaleSelection = useAppStore(state => state.scaleSelection);
  // PR-14: Actions de mirror/flip
  const flipSelection = useAppStore(state => state.flipSelection);
  // PR-16: Actions de grouping
  const groupSelection = useAppStore(state => state.groupSelection);
  const ungroupSelection = useAppStore(state => state.ungroupSelection);
  // PR-17: Actions de lock/hide
  const toggleLockSelection = useAppStore(state => state.toggleLockSelection);
  const toggleHideSelection = useAppStore(state => state.toggleHideSelection);

  if (!selectionBounds || selectionIds.length === 0) {
    return <></>;
  }

  // PR-09: Position du menu pan-aware (juste en dessous de la sélection)
  const menuX = selectionBounds.x;
  const menuY = selectionBounds.y + selectionBounds.height + 8; // 8px de marge

  // PR-09: Calcul position HTML "vraie" avec stagePosition
  const leftPx = menuX * zoomLevel + canvasOffset.x + stagePosition.x;
  const topPx = menuY * zoomLevel + canvasOffset.y + stagePosition.y;

  const handleCenter = () => {
    // PR-09: Centrer les formes sélectionnées sur le canvas (multi-select)
    if (selectionIds.length === 0) return;

    beginGesture('property', selectionIds);

    const updates = selectionIds
      .map(id => {
        const shape = shapes.find(s => s.id === id);
        if (!shape) return null;
        const shapeWidth = shape.width || 100;
        const shapeHeight = shape.height || 100;
        return {
          id,
          updates: {
            x: (canvasWidth - shapeWidth) / 2,
            y: (canvasHeight - shapeHeight) / 2,
          },
        };
      })
      .filter((u): u is NonNullable<typeof u> => u !== null);

    if (updates.length > 0) {
      updateShapes(updates);
      logger.info(`✅ ${updates.length} forme(s) centrée(s) sur le canvas`);
    }

    endGesture();
  };

  const handleTidyUp = () => {
    // PR-09: Organiser les formes sélectionnées en grille (multi-select)
    const selectedShapes = shapes.filter(s => selectionIds.includes(s.id));
    if (selectedShapes.length === 0) return;

    // Calculer une grille pour organiser les formes
    const cols = Math.ceil(Math.sqrt(selectedShapes.length));
    const spacing = 20;
    const cellWidth = (canvasWidth - spacing * (cols + 1)) / cols;
    const cellHeight = cellWidth; // Grille carrée

    beginGesture('property', selectionIds);

    const updates = selectedShapes.map((shape, index) => {
      const row = Math.floor(index / cols);
      const col = index % cols;
      const x = spacing + col * (cellWidth + spacing);
      const y = spacing + row * (cellHeight + spacing);
      return { id: shape.id, updates: { x, y } };
    });

    updateShapes(updates);
    endGesture();

    logger.info(`✅ ${selectedShapes.length} forme(s) organisée(s) en grille`);
  };

  /**
   * Valide qu'une forme est de type image et peut être remplie
   * Fonction pure pour la validation
   */
  const validateImageShape = (
    shape: ShapeData | undefined
  ): shape is ShapeData & { type: 'image' } => {
    return !!shape && shape.type === 'image';
  };

  /**
   * Convertit un fichier en DataURL de manière sécurisée
   * Fonction pure pour la conversion
   */
  const convertFileToDataURL = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event: ProgressEvent<FileReader>) => {
        const result = event.target?.result;
        if (typeof result === 'string') {
          resolve(result);
        } else {
          reject(new Error('Impossible de convertir le fichier en DataURL'));
        }
      };

      reader.onerror = () => {
        reject(new Error('Erreur lors de la lecture du fichier'));
      };

      reader.readAsDataURL(file);
    });
  };

  /**
   * Met à jour la source d'une forme image avec une nouvelle image
   * Fonction pure pour la transformation
   */
  const updateImageShapeSource = (
    shapeId: string,
    imageDataURL: string,
    updateFn: (id: string, updates: Partial<ShapeData>) => void
  ): void => {
    updateFn(shapeId, {
      src: imageDataURL,
    });
  };

  /**
   * Remplit une forme image avec un fichier sélectionné par l'utilisateur
   * Gestion d'erreur granulaire avec fallback gracieux
   */
  const handleFillImage = useCallback(() => {
    // PR-09: Fill image pour la première shape sélectionnée (ou primary)
    const primaryId = selectionIds.length > 0 ? selectionIds[0] : null;
    if (!primaryId) return;

    const shape = shapes.find(s => s.id === primaryId);

    // Validation précoce : vérifier que la forme est une image
    if (!validateImageShape(shape)) {
      logger.warn('Fill Image: La forme sélectionnée doit être de type image', {
        shapeId: primaryId,
        shapeType: shape?.type,
      });
      return;
    }

    // Ouvrir le sélecteur de fichier via FileUploadService
    const fileUploadService = FileUploadService.getInstance();

    fileUploadService.openImageSelector(
      async (file: File) => {
        try {
          // Convertir le fichier en DataURL
          const imageDataURL = await convertFileToDataURL(file);

          // Mettre à jour la forme avec la nouvelle image
          updateImageShapeSource(shape.id, imageDataURL, updateShape);

          logger.info('✅ Image remplie avec succès dans la forme', {
            shapeId: shape.id,
            fileName: file.name,
            fileSize: file.size,
          });
        } catch (error) {
          // Gestion d'erreur granulaire : erreur de conversion
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Erreur inconnue lors de la conversion du fichier';

          logger.error("❌ Erreur lors du remplissage de l'image", {
            error: errorMessage,
            shapeId: shape.id,
            fileName: file.name,
          });

          // Fallback gracieux : l'utilisateur peut réessayer
          // L'erreur est loggée mais l'application continue de fonctionner
        }
      },
      (error: Error) => {
        // Gestion d'erreur granulaire : erreur d'ouverture du sélecteur
        logger.error("❌ Erreur lors de l'ouverture du sélecteur de fichier", {
          error: error.message,
          shapeId: shape.id,
        });

        // Fallback gracieux : l'erreur est loggée, l'utilisateur peut réessayer
      }
    );
  }, [selectionIds, shapes, updateShape]);

  // PR-09: Actions multi-select
  const handleDuplicate = () => {
    if (selectionIds.length > 0) {
      duplicateShapes(selectionIds);
      logger.info(`✅ ${selectionIds.length} forme(s) dupliquée(s)`);
    }
  };

  const handleDelete = () => {
    if (selectionIds.length > 0) {
      deleteShapes(selectionIds);
      logger.info(`✅ ${selectionIds.length} forme(s) supprimée(s)`);
    }
  };

  // CORRIGÉ: Handlers pour Z-Index (bringToFront/sendToBack)
  const moveShapeToTop = useAppStore(state => state.moveShapeToTop);
  const moveShapeToBottom = useAppStore(state => state.moveShapeToBottom);

  // PR-09: Z-index pour multi-select
  const handleMoveToFront = () => {
    if (selectionIds.length > 0) {
      beginGesture('property', selectionIds);
      selectionIds.forEach(id => moveShapeToTop(id));
      endGesture();
      logger.info(`⬆️ ${selectionIds.length} shape(s) mise(s) au premier plan`);
    }
  };

  const handleMoveToBack = () => {
    if (selectionIds.length > 0) {
      beginGesture('property', selectionIds);
      selectionIds.forEach(id => moveShapeToBottom(id));
      endGesture();
      logger.info(`⬇️ ${selectionIds.length} shape(s) mise(s) à l'arrière-plan`);
    }
  };

  return (
    <div
      className="floating-action-pill"
      style={{
        position: 'absolute',
        left: `${leftPx}px`,
        top: `${topPx}px`,
        transform: 'translateX(-50%)',
      }}
    >
      <button className="floating-action-item" onClick={handleCenter} title="Centrer">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <line x1="12" y1="2" x2="12" y2="22" />
          <line x1="2" y1="12" x2="22" y2="12" />
        </svg>
        <span>Center</span>
      </button>

      <button className="floating-action-item" onClick={handleTidyUp} title="Tidy Up">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
        </svg>
        <span>Tidy Up</span>
      </button>

      <button className="floating-action-item" onClick={handleFillImage} title="Fill Image">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
        <span>Fill Image</span>
      </button>

      <button className="floating-action-item" onClick={handleDuplicate} title="Dupliquer">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
        </svg>
        <span>Duplicate</span>
      </button>

      <button className="floating-action-item" onClick={handleDelete} title="Supprimer">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
        </svg>
        <span>Delete</span>
      </button>

      {/* PR-12: Boutons d'alignement */}
      <div className="floating-action-divider" />
      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
        <button
          className="floating-action-item"
          onClick={() => alignSelection('left')}
          title="Align Left"
        >
          <AlignLeft className="w-4 h-4" />
        </button>
        <button
          className="floating-action-item"
          onClick={() => alignSelection('centerX')}
          title="Align Center"
        >
          <AlignCenter className="w-4 h-4" />
        </button>
        <button
          className="floating-action-item"
          onClick={() => alignSelection('right')}
          title="Align Right"
        >
          <AlignRight className="w-4 h-4" />
        </button>
        <button
          className="floating-action-item"
          onClick={() => alignSelection('top')}
          title="Align Top"
        >
          <AlignTop className="w-4 h-4" />
        </button>
        <button
          className="floating-action-item"
          onClick={() => alignSelection('centerY')}
          title="Align Middle"
        >
          <AlignVerticalCenter className="w-4 h-4" />
        </button>
        <button
          className="floating-action-item"
          onClick={() => alignSelection('bottom')}
          title="Align Bottom"
        >
          <AlignBottom className="w-4 h-4" />
        </button>
      </div>

      {/* PR-12: Boutons de distribution (seulement si >= 3 shapes) */}
      {selectionIds.length >= 3 && (
        <>
          <div className="floating-action-divider" />
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              className="floating-action-item"
              onClick={() => distributeSelection('horizontal')}
              title="Distribute Horizontal"
            >
              ⇆
            </button>
            <button
              className="floating-action-item"
              onClick={() => distributeSelection('vertical')}
              title="Distribute Vertical"
            >
              ⇅
            </button>
          </div>
        </>
      )}

      {/* PR-13: Boutons de rotation et scale */}
      <div className="floating-action-divider" />
      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
        <button
          className="floating-action-item"
          onClick={() => rotateSelection(-15)}
          title="Rotate -15°"
        >
          ⟲
        </button>
        <button
          className="floating-action-item"
          onClick={() => rotateSelection(15)}
          title="Rotate +15°"
        >
          ⟳
        </button>
        <button
          className="floating-action-item"
          onClick={() => scaleSelection(0.9)}
          title="Scale -10%"
        >
          ➖
        </button>
        <button
          className="floating-action-item"
          onClick={() => scaleSelection(1.1)}
          title="Scale +10%"
        >
          ➕
        </button>
      </div>

      {/* PR-14: Boutons de mirror/flip */}
      <div className="floating-action-divider" />
      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
        <button
          className="floating-action-item"
          onClick={() => flipSelection('horizontal', 'mirror')}
          title="Mirror Horizontal (layout)"
        >
          ⇋
        </button>
        <button
          className="floating-action-item"
          onClick={() => flipSelection('vertical', 'mirror')}
          title="Mirror Vertical (layout)"
        >
          ⇅
        </button>
        <button
          className="floating-action-item"
          onClick={() => flipSelection('horizontal', 'flip')}
          title="Flip Horizontal (layout + orientation)"
        >
          ⟷
        </button>
        <button
          className="floating-action-item"
          onClick={() => flipSelection('vertical', 'flip')}
          title="Flip Vertical (layout + orientation)"
        >
          ⟷↕
        </button>
      </div>

      {/* PR-16: Boutons de grouping */}
      <div className="floating-action-divider" />
      {selectionIds.length >= 2 && (
        <button
          className="floating-action-item"
          onClick={() => groupSelection()}
          title="Group (Ctrl+G)"
        >
          🧩
        </button>
      )}
      {selectionIds.some(id => shapes.find(x => x.id === id)?.groupId) && (
        <button
          className="floating-action-item"
          onClick={() => ungroupSelection()}
          title="Ungroup (Ctrl+Shift+G)"
        >
          🧨
        </button>
      )}

      {/* PR-17: Boutons Lock/Hide */}
      <div className="floating-action-divider" />
      <button
        className="floating-action-item"
        onClick={() => toggleLockSelection()}
        title="Toggle Lock (Ctrl+L)"
      >
        🔒
      </button>
      <button
        className="floating-action-item"
        onClick={() => toggleHideSelection()}
        title="Toggle Hide (Ctrl+Shift+H)"
      >
        <Eye className="w-4 h-4" />
      </button>

      {/* CORRIGÉ: Boutons pour changer de calque */}
      <div className="floating-action-divider" />
      <button
        className="floating-action-item"
        onClick={handleMoveToFront}
        title="Mettre au premier plan"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M12 2L2 7l10 5 10-5-10-5z" />
          <path d="M2 17l10 5 10-5" />
          <path d="M2 12l10 5 10-5" />
        </svg>
        <span>Premier plan</span>
      </button>
      <button
        className="floating-action-item"
        onClick={handleMoveToBack}
        title="Mettre à l'arrière-plan"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M12 22L2 17l10-5 10 5-10 5z" />
          <path d="M2 7l10 5 10-5" />
          <path d="M2 12l10 5 10-5" />
        </svg>
        <span>Arrière-plan</span>
      </button>
    </div>
  );
}

export default FloatingActionMenu;
